import {CommonModule} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faCheck, faCopy, faSquarePlus, faSquareXmark} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';
import {ApiService} from '../../services/api.service';
import {CopyButtonComponent} from '../../shared/copy-button/copy-button.component';

interface BridgeTypeOption {
    value: string;
    label: string;
    max_lpr_supported: number;
    price: number;
}

type BridgeCredential = {
    id: number;
    masked_key: string;
    is_active: boolean;
    last_used_at: string | null;
    created_at: string;
    _revoking?: boolean;
};

@Component({
    selector: 'app-edit-bridge',
    imports: [CommonModule, ReactiveFormsModule, FontAwesomeModule, CopyButtonComponent],
    templateUrl: './edit-bridge.component.html',
    styleUrl: './edit-bridge.component.css'
})
export class EditBridgeComponent implements OnInit {
    updateBridgeForm!: FormGroup;
    orgId!: number;
    orgName = '';
    bridgeTypes: BridgeTypeOption[] = [];
    credentials: BridgeCredential[] = [];
    bridgePk = '';
    showSuccessModal = false;
    generatedRawKey = '';

    constructor(
        private apiService: ApiService,
        private router: Router,
        private toastr: ToastrService,
        private route: ActivatedRoute,
        private fb: FormBuilder,
        library: FaIconLibrary
    ) {
        library.addIcons(faSquareXmark, faSquarePlus, faCopy, faCheck);
    }

    ngOnInit(): void {
        this.bridgePk = this.route.snapshot.paramMap.get('pk') || '';
        this.route.queryParams.subscribe(params => {
            this.orgId = Number(params['org_id']);
            this.orgName = params['name'] || '';
        });

        this.loadBridgeTypes();
        this.getBridgeDetail(this.bridgePk);
    }

    loadBridgeTypes() {
        this.apiService.getBridgeTypes().subscribe({
            next: (types: BridgeTypeOption[]) => {
                this.bridgeTypes = types;
            },
            error: () => {
                this.toastr.error('Unable to load bridge types.', 'Error');
            }
        });
    }

    getBridgeDetail(pk: string | null) {
        if (!pk) {
            return;
        }

        this.apiService.getBridge(pk, this.orgId).subscribe({
            next: (res: any) => {
                this.updateBridgeForm = this.fb.group({
                    bridgeId: [{value: res['id'], disabled: true}],
                    bridgeName: [res['bridge_name'], Validators.required],
                    bridgeEsn: [res['bridge_esn'] || '', Validators.required],
                    serialNumber: [res['serial_number'] || '', Validators.required],
                    bridgeType: [res['bridge_type'], Validators.required],
                    organization: [{value: res['organization'], disabled: true}, Validators.required]
                });
                this.loadCredentials();
            },
            error: () => {
                this.toastr.error('Unable to load bridge details.', 'Error');
            }
        });
    }

    loadCredentials() {
        this.apiService.getBridgeCredentials(this.bridgePk, this.orgId).subscribe({
            next: (credentials: BridgeCredential[]) => {
                this.credentials = credentials;
            },
            error: () => {
                this.toastr.error('Unable to load bridge credentials.', 'Error');
            }
        });
    }

    onEsnInput(event: Event) {
        const input = event.target as HTMLInputElement;
        const upper = input.value.toUpperCase();
        if (upper !== input.value) {
            this.updateBridgeForm.get('bridgeEsn')?.setValue(upper, {emitEvent: false});
        }
    }

    onSerialNumberInput(event: Event) {
        const input = event.target as HTMLInputElement;
        const upper = input.value.toUpperCase();
        if (upper !== input.value) {
            this.updateBridgeForm.get('serialNumber')?.setValue(upper, {emitEvent: false});
        }
    }

    onSubmit() {
        if (!this.updateBridgeForm?.valid) {
            this.validateAllFormFields(this.updateBridgeForm);
            return;
        }

        const payload = {
            bridge_name: this.updateBridgeForm.get('bridgeName')?.value,
            bridge_esn: this.updateBridgeForm.get('bridgeEsn')?.value,
            serial_number: this.updateBridgeForm.get('serialNumber')?.value,
            bridge_type: this.updateBridgeForm.get('bridgeType')?.value
        };

        this.apiService.updateBridge(this.bridgePk, this.orgId, payload).subscribe({
            next: () => {
                this.toastr.success('Bridge Updated successfully.', 'Success');
                this.router.navigate(['/view-bridges'], {
                    queryParams: {id: this.orgId, name: this.orgName}
                });
            },
            error: (err) => this.handleFormErrors(err)
        });
    }

    createCredential() {
        this.apiService.createBridgeCredential(this.bridgePk, this.orgId).subscribe({
            next: (response: any) => {
                this.generatedRawKey = response?.raw_key ?? '';
                this.showSuccessModal = !!this.generatedRawKey;
                this.loadCredentials();
            },
            error: () => {
                this.toastr.error('Unable to create credential.', 'Error');
            }
        });
    }

    revokeCredential(credential: BridgeCredential) {
        credential._revoking = true;
        this.apiService.revokeBridgeCredential(this.bridgePk, credential.id, this.orgId).subscribe({
            next: () => {
                credential._revoking = false;
                credential.is_active = false;
                this.toastr.success('Credential revoked successfully.', 'Success');
                this.loadCredentials();
            },
            error: () => {
                credential._revoking = false;
                this.toastr.error('Unable to revoke credential.', 'Error');
            }
        });
    }

    closeSuccessModal() {
        this.showSuccessModal = false;
        this.generatedRawKey = '';
    }

    formatRelativeTime(value: string | null | undefined): string {
        if (!value) {
            return '-';
        }
        const date = new Date(value);
        const diffMs = Date.now() - date.getTime();
        const diffMinutes = Math.floor(diffMs / 60000);
        if (diffMinutes < 1) {
            return 'Just now';
        }
        if (diffMinutes < 60) {
            return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
        }
        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) {
            return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
        }
        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    }

    handleFormErrors(err: any) {
        const errorBody = err['error'] || {};
        const fieldMap: Record<string, string> = {
            bridge_name: 'Bridge Name',
            bridge_type: 'Bridge Type',
            organization_id: 'Organization',
            bridge_esn: 'Bridge ESN',
            serial_number: 'Serial Number'
        };

        Object.keys(fieldMap).forEach(field => {
            if (errorBody[field]) {
                errorBody[field].forEach((message: string) => {
                    this.toastr.error(message, fieldMap[field]);
                });
            }
        });

        if (errorBody['non_field_errors']) {
            errorBody['non_field_errors'].forEach((message: string) => {
                this.toastr.error(message, 'Error');
            });
        }

        if (
            !errorBody['bridge_name'] &&
            !errorBody['bridge_type'] &&
            !errorBody['organization_id'] &&
            !errorBody['bridge_esn'] &&
            !errorBody['serial_number'] &&
            !errorBody['non_field_errors'] &&
            err['status'] !== 403 &&
            err.status !== 0
        ) {
            this.toastr.error('Something went wrong!', 'Error');
        }
    }

    validateAllFormFields(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach(field => {
            const control = formGroup.get(field);
            if (control instanceof FormGroup) {
                this.validateAllFormFields(control);
            } else {
                control?.markAsTouched({onlySelf: true});
            }
        });
    }

    onCancel() {
        this.router.navigate(['/view-bridges'], {
            queryParams: {id: this.orgId, name: this.orgName}
        });
    }
}
